/**
 * Shared table frame: headings that survive an empty table, and one place for column info.
 *
 * THREE PROBLEMS THIS SOLVES, the first two found by reviewing screens with nothing in them.
 *
 * 1. AN EMPTY TABLE MUST STILL SHOW ITS COLUMNS.
 *    Every table in this console used to replace itself with a single sentence when it had
 *    no rows — which on a fresh install is the entire screen: no column names, no shape, no
 *    way for an admin to know what will appear once data arrives.
 *
 *    Polaris cannot do this on its own. IndexTable swaps the WHOLE table for `emptyState` at
 *    itemCount 0, headings included, so the `emptyState` prop has the same defect as a
 *    ternary around the table. The pattern that works is:
 *
 *        <IndexTable itemCount={rows.length || 1} headings={headingsFor(COLS)}>
 *          {rows.length === 0 ? emptyRow(COLS, 'key', 'copy') : rows.map(...)}
 *        </IndexTable>
 *
 *    The synthetic row is counted as one item so IndexTable renders its header row, and the
 *    real emptiness is expressed by the row itself rather than by the count.
 *
 * 2. A COLUMN SHOULD EXPLAIN ITSELF.
 *    `tip` on a column becomes IndexTable's own tooltipContent. Several columns across this
 *    console cannot be read correctly without one — a timestamp that looks like a live check
 *    but is stored, a count that is per retailer rather than per SKU, a badge inferred from
 *    error text rather than reported by anyone.
 *
 * A TRAP, worth knowing before adding a tip to any table: on a SORTABLE column Polaris reads
 * sortToggleLabels[index][direction] unguarded (IndexTable.js:477) and, when that prop is
 * absent, returns the bare sort button — silently discarding tooltipContent with no error.
 * So a sortable table needs sortToggleLabels for its tips to render at all. See
 * OrderManagement.jsx, which carries the labels for exactly this reason.
 *
 * 3. A TABLE SHOULD LET YOU CHOOSE HOW MUCH OF IT TO SEE.
 *    Every table in this console fixed its own row count in a module constant, so the choice
 *    an admin actually wants to make — scan a lot, or read a little — was made once, at build
 *    time, by whoever wrote the file. The two extremes both hurt: a ten-row window turns a
 *    500-job backlog into fifty clicks, and a fifty-row window turns a wide table into the
 *    whole screen. `PageSizeSelect` is that choice, in one place, so the wording and the
 *    options do not drift table to table.
 */

import React from 'react';
import { IndexTable, Box, Text, Select } from '@shopify/polaris';

/**
 * Column defs -> IndexTable headings.
 *
 * `tooltipWidth: 'wide'` because these tips are sentences, not labels. `id` is set from the
 * column key so a heading is addressable in a test without depending on its position.
 */
export const headingsFor = (cols) => cols.map((c) => ({
  id: c.key,
  title: c.title,
  ...(c.alignment ? { alignment: c.alignment } : {}),
  // Forwarded so the flag actually works. SkuMapTable set `hidden: true` on a column for
  // months against a mapping that dropped it, so the heading it meant to hide from sight
  // was visible the whole time and its comment described the opposite.
  ...(c.hidden ? { hidden: true } : {}),
  ...(c.tip ? { tooltipContent: c.tip, tooltipWidth: 'wide' } : {}),
}));

/**
 * The one row that keeps a table's headings visible when it has nothing to show.
 *
 * Spans every column, so it reads as one message rather than as a row of blanks.
 *
 * `message` is the caller's, deliberately: "nothing here yet", "nothing matches your
 * filters" and "we could not load this" are three different statements, and a shared
 * default would flatten them into the vaguest one.
 */
export const emptyRow = (cols, key, message) => (
  <IndexTable.Row id={`empty-${key}`} position={0}>
    <IndexTable.Cell colSpan={cols.length}>
      <Box padding="500">
        <Text alignment="center" tone="subdued" variant="bodySm">{message}</Text>
      </Box>
    </IndexTable.Cell>
  </IndexTable.Row>
);

/** itemCount for a table that must keep its headings when empty. */
export const itemCountFor = (rows) => (Array.isArray(rows) ? rows.length : 0) || 1;

/**
 * The row counts on offer. 5 for reading one thing closely, 50 for scanning a backlog.
 *
 * 20 is here because several tables already shipped at 20 — Access Requests, Invoices,
 * Brand Configuration's orders — and an option list that omits a size a table is ALREADY
 * showing is worse than an odd-looking list. Polaris `Select` falls back to the FIRST
 * option's label when the value matches none of them, so those tables rendered 20 rows under
 * a control reading "5": the number on screen and the number in the request disagreeing,
 * with the control itself as the liar.
 *
 * Values are strings because Polaris `Select` round-trips its value as a string, and a mixed
 * list would silently fail to match the current selection. `PageSizeSelect` hands the caller a
 * NUMBER, so nothing downstream has to remember this.
 */
export const PAGE_SIZE_OPTIONS = [
  { label: '5', value: '5' },
  { label: '10', value: '10' },
  { label: '20', value: '20' },
  { label: '25', value: '25' },
  { label: '50', value: '50' },
];

/**
 * What a table shows before anyone chooses.
 *
 * FIVE. The connector page stacks seven tables, and at ten rows each the ones an admin
 * actually came for — the brand connections, the queue — sat two screens below the fold. A
 * short default and a visible pager reads better than a long list nobody scrolled to the end
 * of; anyone who wants more raises it per table and that choice sticks.
 */
export const DEFAULT_PAGE_SIZE = 5;

/**
 * The rows-per-page control.
 *
 * `labelInline` on purpose: this sits in a table's footer beside a pager, where a stacked label
 * would be taller than the pager next to it, and a bare unlabelled Select showing "10" beside
 * "Page 2 of 7" reads as a page jump rather than a row count.
 *
 * `onChange` is called with a Number, not the Select's string. Callers do arithmetic with it —
 * `Math.ceil(total / pageSize)` for the page count, `(page - 1) * pageSize` for the offset —
 * and a string silently turns the first into a string-division NaN path and the second into
 * concatenation. `slice` itself would cope, since it coerces a numeric string, so the risk is
 * not the slice but everything around it. Coercing once here beats every table remembering.
 *
 * Changing the size must reset the caller to page 1; that is the caller's state, so it stays
 * the caller's job — see QueueTable, which resets in the same effect as its filters.
 */
export const PageSizeSelect = ({ value, onChange }) => (
  <Select
    label="Rows per page"
    labelInline
    options={PAGE_SIZE_OPTIONS}
    value={String(value)}
    onChange={(next) => onChange(Number(next))}
  />
);
